/**
 * IntakeEventDetailPage — `/intake-events/:id` (Chunks 9.T.3 + 9.T.4).
 *
 * Page détail d'un intake_event broker (orphan ou lié à un lien d'intake F2 —
 * la page est tenant-wide, pas couplée à `linkId`).
 *
 * Vue Wave 9.T.4 :
 *   - Panel classification (`<ClassificationPanel>`) monté conditionnellement
 *     selon `event.payload.classification` :
 *       - idle (jamais classifié) → bouton « Analyser maintenant »
 *       - loading (mutation en vol) → spinner
 *       - soft-fail → bouton « Analyser à nouveau »
 *       - success → suggestions candidate_company/contact + evidence
 *   - Auto-classify one-shot via query param `?auto_classify=1` posé par
 *     `IntakeEventUploadPage` post-upload. Search param cleared après trigger
 *     (`setSearchParams((previous) => { const next = new URLSearchParams(previous); next.delete("auto_classify"); return next; }, { replace: true })`) — un refresh manuel n'auto-
 *     déclenche pas.
 *   - MaterializeEventDialog remplace ResolveEventDialog sur CETTE surface
 *     (`/intake-events/:id`). `/intake-links/:id` (IntakeEventCard) continue
 *     à utiliser ResolveEventDialog inchangé (cohabitation stricte PO Q7).
 *
 * États affichés (5) :
 *   1. `pending_classification` + pas encore analysé (idle)
 *   2. Analyse en cours (mutation isPending)
 *   3. `pending_classification` + soft-fail → retry visible
 *   4. `pending_resolution` (post-success) → suggestions + boutons Materialize / Reject
 *   5. Terminal (`resolved`|`rejected`|`superseded`) → actions retirées
 *
 * Anti-leak strict :
 *   - Aucun `storage_path`/`storage_bucket`/`sha256`/`classifier_version`/
 *     `raw_llm_response`/`input_tokens`/`output_tokens`/`cost_usd`/`duration_ms`
 *     jamais affiché (backend sanitize wire ADR D-NEW-C + Q8).
 *   - Pas de `console.log` de l'event ou de la classification.
 */

import type { ClassificationPayload } from "@kollectivia/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ExtractionReviewPanel } from "../../components/extraction/ExtractionReviewPanel";
import { SourceDocument } from "../../components/SourceDocument";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useClassifyIntakeEvent, useIntakeEvent } from "../../lib/queries/intakeEvents";
import { useEventDocumentLinks } from "../../lib/queries/intakeLinks";
import { RejectEventDialog } from "../intake-links/_parts/RejectEventDialog";
import { ResolveEventDialog } from "../intake-links/_parts/ResolveEventDialog";

import { ClassificationPanel } from "./_parts/ClassificationPanel";
import { getFormResponseId, IntakeFormResponsePanel } from "./_parts/IntakeFormResponsePanel";
import { MaterializeWizard } from "./_parts/materialize-wizard/MaterializeWizard";
import { PrivacyConsentEvidence } from "./_parts/PrivacyConsentEvidence";
import { useAutoClassifyOnce } from "./_parts/useAutoClassifyOnce";
import { VoluntaryEmailPanel } from "./_parts/VoluntaryEmailPanel";

type TerminalStatus = "resolved" | "rejected" | "superseded";
const TERMINAL_STATUSES: ReadonlyArray<TerminalStatus> = ["resolved", "rejected", "superseded"];

export function IntakeEventDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const eventQ = useIntakeEvent(id);
  const docLinksQ = useEventDocumentLinks(id);
  const classify = useClassifyIntakeEvent();

  const [materializeOpen, setMaterializeOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  // Auto-classify one-shot — logique factorisée dans `useAutoClassifyOnce`
  // (hook pur testable en isolation). Le hook garde son propre ref one-shot
  // interne + gère l'invariance F5-safe (clear searchParam avant trigger).
  const isProvider = eventQ.data?.payload?.kind === "provider_email";
  const isVoluntary = isProvider || eventQ.data?.payload?.kind === "voluntary_email";
  const shouldAutoClassify = !isVoluntary && searchParams.get("auto_classify") === "1";
  const event = eventQ.data;
  const classification =
    (event?.payload?.classification as ClassificationPayload | undefined) ?? null;

  const clearSearchParam = useCallback(
    () =>
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete("auto_classify");
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const triggerClassify = useCallback(() => {
    if (id) classify.mutate({ eventId: id });
  }, [id, classify]);

  useAutoClassifyOnce({
    eventId: id,
    status: event?.status,
    hasClassification: !!classification,
    isClassifying: classify.isPending,
    shouldAutoClassify,
    onClearSearchParam: clearSearchParam,
    onTrigger: triggerClassify,
  });

  if (!id) {
    return (
      <PageShell>
        <p className="text-sm text-red-700">{t("intake_events.detail.errors.missing_id")}</p>
      </PageShell>
    );
  }

  if (eventQ.isLoading) {
    return (
      <PageShell>
        <p className="text-sm text-slate-500" aria-live="polite">
          {t("intake_events.detail.loading")}
        </p>
      </PageShell>
    );
  }

  if (eventQ.error) {
    const is404 = eventQ.error instanceof ApiException && eventQ.error.status === 404;
    const msg = is404
      ? t("intake_events.detail.errors.not_found")
      : getApiErrorMessage(eventQ.error, lang) || t("errors.generic");
    return (
      <PageShell>
        <div
          role="alert"
          aria-live="polite"
          className="rounded-lg border border-red-200 bg-red-50/60 px-3.5 py-2.5 text-sm text-red-800"
        >
          {msg}
        </div>
      </PageShell>
    );
  }

  if (!event) return null;

  const status = event.status;
  const isTerminal = TERMINAL_STATUSES.includes(status as TerminalStatus);

  const files = event.payload?.files ?? [];

  // 1e-A — viewer read-only de la réponse conseiller (saisie directe 1d).
  // Rendu seulement si l'event désigne une form_response (origin + UUID valide).
  // Visible en tous statuts (y compris resolved) comme trace d'audit.
  const formResponseId = getFormResponseId(event.payload);

  // Handler bouton « Analyser maintenant » / « Analyser à nouveau » depuis
  // ClassificationPanel (idle + soft-fail states).
  const onTriggerClassify = () => {
    if (id && !classify.isPending) classify.mutate({ eventId: id });
  };

  return (
    <PageShell>
      <header className="space-y-1">
        <nav aria-label={t("common.breadcrumb")} className="mb-1.5 text-sm">
          <Link
            to={`/intake-events${searchParams.get("receipt_cursor") ? `?cursor=${encodeURIComponent(searchParams.get("receipt_cursor")!)}` : ""}`}
            className="focus-visible:ring-brand-500 rounded text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("reception_flow.receipts")}
          </Link>
          <span className="mx-2 text-slate-300" aria-hidden="true">
            ›
          </span>
          <span className="text-slate-900">
            {t(
              isVoluntary
                ? "voluntary_email.receipt_title"
                : "intake_events.detail.breadcrumb.current",
            )}
          </span>
        </nav>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {t(isVoluntary ? "voluntary_email.receipt_title" : "intake_events.detail.title")}
        </h1>
      </header>

      <section className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-7">
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Field
            label={t("intake_events.detail.fields.status")}
            value={<StatusBadge status={status} />}
          />
          <Field
            label={t("intake_events.detail.fields.source_kind")}
            value={t(
              isProvider
                ? "voluntary_email.provider_source"
                : isVoluntary
                  ? "voluntary_email.source"
                  : `intake_events.detail.source_kinds.${event.source_kind}`,
            )}
          />
          <Field
            label={t("intake_events.detail.fields.created_at")}
            value={formatDateTime(event.created_at, lang)}
          />
          <Field
            label={t("intake_events.detail.fields.updated_at")}
            value={formatDateTime(event.updated_at, lang)}
          />
        </dl>

        {event.source_kind === "form_submission" && (
          <PrivacyConsentEvidence consent={event.payload?.consent} />
        )}

        {/* 9.T.4 — Panel classification. Monté uniquement si non-terminal. */}
        {!isTerminal && !isVoluntary && (
          <ClassificationPanel
            classification={classification}
            isClassifying={classify.isPending}
            onTriggerClassify={onTriggerClassify}
          />
        )}

        {/* Les PDF signés sont montrés avec la réponse, pas comme uploads. */}
        {isVoluntary && typeof event.payload?.admission_id === "string" && (
          <VoluntaryEmailPanel
            key={event.payload.admission_id}
            admissionId={event.payload.admission_id}
          />
        )}
        {!isVoluntary && (!formResponseId || files.length > 0) && (
          <div>
            <h2 className="mb-2 text-sm font-semibold text-slate-800">
              {t("intake_events.detail.files.title", { count: files.length })}
            </h2>
            {docLinksQ.isLoading ? (
              <p className="text-xs text-slate-500" aria-live="polite">
                {t("intake_events.detail.files.loading")}
              </p>
            ) : (docLinksQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-500">{t("intake_events.detail.files.empty")}</p>
            ) : (
              <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {docLinksQ.data?.items.map((dl) => (
                  <li
                    key={dl.id}
                    className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 text-sm text-slate-700 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <SourceDocument
                        key={dl.file_id}
                        fileId={dl.file_id}
                        name={dl.display_name ?? dl.file_id}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {isTerminal && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700"
          >
            {t(
              isVoluntary && status === "resolved"
                ? "voluntary_email.context_linked"
                : `intake_events.detail.terminal.${status}`,
            )}
          </div>
        )}

        {/* Post-smoke 9.T.4 — chaîne de navigation depuis intake_event terminal
            (resolved). Rend visible le lien vers la company et le project
            créés par materialize, pour éviter que le broker « perde » ces
            entités après résolution. Utilise les FK denormalisées sur la row
            (resolved_company_id / resolved_project_id) — anti-fragilité :
            même si `dedup_decision` future-formatée change, les FK sont
            stables sur la row de vérité DB. */}
        {isTerminal && (event.resolved_project_id || event.resolved_company_id) && (
          <div
            className="border-brand-200 bg-brand-50/50 rounded-lg border px-3.5 py-2.5"
            data-testid="intake-event-related-entities"
          >
            <h2 className="text-brand-800 text-xs font-semibold uppercase tracking-wide">
              {t("intake_events.detail.related_entities.title")}
            </h2>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-sm">
              {event.resolved_company_id && (
                <Link
                  to={`/companies/${event.resolved_company_id}`}
                  className="focus-visible:ring-brand-500 text-brand-700 hover:text-brand-900 rounded underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  data-testid="intake-event-related-company-link"
                >
                  {t("intake_events.detail.related_entities.view_company")}
                </Link>
              )}
              {event.resolved_project_id && (
                <Link
                  to={`/projects/${event.resolved_project_id}`}
                  className="focus-visible:ring-brand-500 text-brand-700 hover:text-brand-900 rounded underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  data-testid="intake-event-related-project-link"
                >
                  {t("intake_events.detail.related_entities.view_project")}
                </Link>
              )}
            </div>
          </div>
        )}

        {!isTerminal && (
          <div className="flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={() => setRejectOpen(true)}
              className="focus-visible:ring-brand-500 inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("intake_events.detail.actions.reject")}
            </button>
            <button
              type="button"
              data-guide-target={isVoluntary ? "email-context" : undefined}
              onClick={() => setMaterializeOpen(true)}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t(
                isVoluntary
                  ? "voluntary_email.context"
                  : "intake_events.detail.actions.materialize",
              )}
            </button>
          </div>
        )}
      </section>

      {/* 1e-A — Réponse du formulaire (saisie directe conseiller) en lecture seule.
          Rendue pour les events issus d'une form_response, en tous statuts. */}
      {formResponseId && <IntakeFormResponsePanel formResponseId={formResponseId} />}

      {/* 9.T.5.v3.3 — Review d'extraction visible UNIQUEMENT quand
          l'event est resolved (décision PO Q6). Le composant gère lui-même
          l'état vide propre si aucune extraction ou aucun champ n'existe
          encore (post-materialize mais avant pass extract_details, ou pass
          en cours). */}
      {!isVoluntary && event.status === "resolved" && (
        <div className="mt-6">
          <ExtractionReviewPanel parentKind="intake_event" parentId={id} />
        </div>
      )}

      {/* 9.T.5.v3.6 — MaterializeWizard remplace le legacy MaterializeEventDialog
          monolithique. 6 steps guidés + review_snapshot (backend v3.5). */}
      {isVoluntary ? (
        <ResolveEventDialog
          receiptKind="voluntary_email"
          open={materializeOpen}
          eventId={id}
          linkId={event.intake_link_id}
          onClose={() => setMaterializeOpen(false)}
          onResolved={() => setMaterializeOpen(false)}
        />
      ) : (
        <MaterializeWizard
          open={materializeOpen}
          eventId={id}
          classification={classification}
          formResponseId={formResponseId}
          onClose={() => setMaterializeOpen(false)}
          onMaterialized={() => setMaterializeOpen(false)}
        />
      )}
      {/* RejectEventDialog conservé pour la voie « rejeter » (parité 9.G.1) */}
      <RejectEventDialog
        open={rejectOpen}
        eventId={id}
        linkId={event.intake_link_id}
        onClose={() => setRejectOpen(false)}
        onRejected={() => setRejectOpen(false)}
      />
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-3xl">{children}</div>;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-900">{value}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone: Record<string, string> =
    status === "resolved"
      ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" }
      : status === "rejected"
        ? { bg: "bg-red-50", border: "border-red-200", text: "text-red-800" }
        : status === "superseded"
          ? { bg: "bg-slate-100", border: "border-slate-200", text: "text-slate-700" }
          : status === "pending_resolution"
            ? { bg: "bg-brand-50", border: "border-brand-200", text: "text-brand-800" }
            : { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" };
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${tone.bg} ${tone.border} ${tone.text}`}
    >
      {t(`intake_events.detail.statuses.${status}`)}
    </span>
  );
}

function formatDateTime(iso: string, lang: "fr" | "en"): string {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return iso;
  }
}
